'use client';

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgePercent,
  CheckCircle2,
  HeartPulse,
  ShieldCheck,
  ShoppingBag,
  AlertCircle,
} from 'lucide-react';
import { CartItem } from '@/components/CartItem';
import { OutOfStockCartItem } from '@/components/OutOfStockCartItem';
import { Button } from '@/components/ui/Button';
import { useCart } from '@/providers/CartProvider';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatPrice } from '@/lib/utils/format';
import { useRouter } from 'next/navigation';
import { CartItemPopulated } from '@/lib/Apis';
import { useBasePath } from '@/providers/BasePathProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useNotification } from '@/providers/NotificationProvider';
import { usePersonalizedProducts } from '@/hooks/usePersonalizedProducts';
import { ProductCard } from '@/components/ProductCard';
import { RecommendedProductsSection } from '@/components/RecommendedProductsSection';
import { Product } from '@/lib/Apis';
import { Modal } from '@/components/ui/Modal';
import { ProductVariantInventoryStatusEnum, ProductVariantPopulatedInventoryStatusEnum } from '@/lib/Apis/models';
import Image from 'next/image';
import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';


function CartItemSkeleton() {
  return (
    <div className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100">
      <Skeleton className="h-24 w-24 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-3 py-1">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <div className="flex items-center justify-between pt-1">
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-6 w-16" />
        </div>
      </div>
    </div>
  );
}

function CartScreenSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="py-8">
          <div className="mb-8 md:mb-12 space-y-2">
            <Skeleton className="h-9 w-52" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Items column */}
            <div className="space-y-4 lg:col-span-2">
              <CartItemSkeleton />
              <CartItemSkeleton />
              <CartItemSkeleton />
            </div>
            {/* Summary column */}
            <div className="lg:col-span-1">
              <div className="rounded-[24px] border-2 border-gray-100 p-6 md:p-8 space-y-5">
                <Skeleton className="h-6 w-36" />
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="border-t border-gray-100 pt-4 flex justify-between">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-7 w-20" />
                  </div>
                </div>
                <Skeleton className="h-12 w-full rounded-full" />
                <Skeleton className="h-12 w-full rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CartScreen() {
  const router = useRouter();
  const { cart, isLoading, isFetching, updateQuantity, removeFromCart, isLoading: cartIsLoading } = useCart();
  const { buildPath } = useBasePath();
  const { isAuthenticated, user } = useAuth();
  const notification = useNotification();

  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [quantityAdjustItems, setQuantityAdjustItems] = useState<any[]>([]);

  // Stock issues: items where quantity exceeds inventory (but not out of stock)
  const stockIssues = useMemo(() => {
    if (!cart?.cartBody?.items) return [];
    return cart.cartBody.items.filter(item => {
      const v = item.productVariantData;
      const inventoryCount = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
      // Check if inventoryStatus is OUT_OF_STOCK or quantity exceeds inventory
      if (v.inventoryStatus === ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK) return true;
      if (inventoryCount > 0 && inventoryCount < item.quantity) return true;
      return false;
    });
  }, [cart]);

  if (isFetching) {
    return <CartScreenSkeleton />;
  }

  if (!cart || cart.cartBody.items.length === 0) {
    return (
      <div className="bg-white">
        <div className="min-h-screen bg-white max-w-6xl mx-auto">
          <div className="container mx-auto px-4 py-8">

            <div className="flex flex-col items-center justify-center py-20">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center space-y-6 max-w-md"
              >
                <div className="flex justify-center">
                  <div className="rounded-full bg-gray-100 p-6">
                    <ShoppingBag className="h-12 w-12 text-hsecondary" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-hsecondary">
                    Your cart is empty
                  </h2>
                  <p className="text-hmuted">
                    Start adding products to your cart to see them here.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => router.push(buildPath('/shop'))}
                    className="rounded-xl border-2 border-hprimary bg-hsecondary text-white px-6 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-80"
                  >
                    Discover products
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>

                <div className="mt-8 space-y-3 pt-6 border-t border-gray-200">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-hsecondary shrink-0 mt-0.5" />
                    <span>Secure checkout process</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Recommended Section - Even when cart is empty */}
        <RecommendedProductsSection
          title="Recommended for you"
          subtitle=""
          initialLimit={4}
          className="border-t border-gray-100 pt-12 mt-12"
        />
      </div>
    );
  }

  const inStockItems = cart.cartBody.items.filter(item => (item.productVariantData.inventoryCount ?? 0) > 0);
  const outOfStockItems = cart.cartBody.items.filter(item => (item.productVariantData.inventoryCount ?? 0) === 0);

  const subtotal = Math.round(inStockItems.reduce((total, item) => total + item.productVariantData.finalPrice * item.quantity, 0) * 100) / 100;
  const shipping = 0;
  const tax = 0;
  const total = subtotal + shipping + tax;
  const itemCount = inStockItems.length;

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement> | React.MouseEvent) => {
    const quantityIssues = stockIssues.filter(item => {
      const v = item.productVariantData;
      const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
      return maxQty > 0 && v.inventoryStatus !== ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;
    });

    if (quantityIssues.length > 0) {
      setQuantityAdjustItems(quantityIssues);
      setShowQuantityModal(true);
      return;
    }

    router.push(buildPath('/checkout'));
  };


  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="py-8">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="font-semibold text-hsecondary tracking-[-1px] md:tracking-[-2px] mb-2 text-3xl md:text-4xl">
              Shopping Cart
            </h1>
            <p className="text-[14px] md:text-[16px] text-hmuted">
              {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shopping Cart Section */}
            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 lg:col-span-2"
            >
              <div className="space-y-4">
                {/* Stock Issues Banner - shows items with quantity issues (not out of stock) */}
                {stockIssues.filter(item => {
                  const v = item.productVariantData;
                  const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
                  return maxQty > 0 && v.inventoryStatus !== ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;
                }).length > 0 && (
                    <div className="border-2 border-amber-300 bg-amber-50 rounded-[24px] overflow-hidden">
                      {/* Header */}
                      <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-amber-200">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-800 text-base leading-tight">
                            Some items exceed available quantity
                          </p>
                          <p className="text-amber-700 text-sm mt-0.5">
                            Adjust the quantity or remove items below to proceed to checkout.
                          </p>
                        </div>
                      </div>

                      {/* Item rows */}
                      <div className="px-6 py-4 space-y-3">
                        {stockIssues
                          .filter(item => {
                            const v = item.productVariantData;
                            const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;
                            return maxQty > 0 && v.inventoryStatus !== ProductVariantPopulatedInventoryStatusEnum.OUTOFSTOCK;
                          })
                          .map(item => {
                            const v = item.productVariantData;
                            const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;

                            return (
                              <div
                                key={item.productVariantId}
                                className="flex items-center gap-3 bg-white border border-amber-200 rounded-2xl p-3"
                              >
                                {/* Thumbnail */}
                                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 shrink-0 border border-amber-100">
                                  <Image
                                    src={v.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                                    alt={v.name}
                                    width={48}
                                    height={48}
                                    className="w-full h-full object-cover"
                                  />
                                </div>

                                {/* Name + availability */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{v.name}</p>
                                  <p className="text-xs font-medium text-amber-600 mt-0.5">
                                    Only {maxQty} available — you have {item.quantity}
                                  </p>
                                </div>

                                {/* Controls - Only Reduce to button, no decrement */}
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Directly reduce to available quantity
                                      updateQuantity(item.productVariantId, maxQty);
                                    }}
                                    disabled={cartIsLoading || item.quantity === maxQty}
                                    className="text-xs font-semibold text-amber-600 border border-amber-200 bg-amber-50 rounded-full px-2.5 py-1.5 hover:bg-amber-100 transition-colors disabled:opacity-50"
                                  >
                                    Reduce to {maxQty}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeFromCart(item.productVariantId)}
                                    disabled={cartIsLoading}
                                    className="text-xs font-semibold text-red-500 border border-red-200 bg-white rounded-full px-2.5 py-1.5 hover:bg-red-50 transition-colors disabled:opacity-50"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                <div className="space-y-4">
                  {inStockItems.length > 0 ? (
                    inStockItems.map((item: CartItemPopulated) => (
                      <CartItem key={item.productVariantId} item={item} />
                    ))
                  ) : outOfStockItems.length > 0 ? (
                    <div className="bg-hprimary/5 border border-hprimary/20 rounded-[24px] p-6 text-center">
                      <p className="text-hsecondary font-medium">All items in your cart are currently out of stock.</p>
                      <p className="text-sm text-hmuted mt-1">Move them to your wishlist or remove them to continue shopping.</p>
                    </div>
                  ) : null}
                </div>

                {outOfStockItems.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <h2 className="text-xl font-bold text-[#2B4B7C]">Recently Unavailable</h2>
                      <span className="bg-red-50 text-red-600 text-[11px] font-bold px-2 py-0.5 rounded-full border border-red-100 uppercase tracking-wider">
                        {outOfStockItems.length} {outOfStockItems.length === 1 ? 'Item' : 'Items'}
                      </span>
                    </div>
                    <p className="text-sm text-hmuted mb-6">
                      These items are out of stock and have been removed from your total. You can move them to your wishlist to buy later.
                    </p>
                    <div className="space-y-4 opacity-75">
                      {outOfStockItems.map((item: CartItemPopulated) => (
                        <OutOfStockCartItem key={item.productVariantId} item={item} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.section>

            {/* Order Summary Section */}
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 h-fit lg:col-span-1"
            >
              <div className="bg-hsecondary/[0.03] md:bg-linear-to-br md:from-hsecondary/10 md:to-hsecondary/10 rounded-[24px] p-6 md:p-8 border-2 border-hsecondary/10 md:border-hsecondary/20 lg:sticky lg:top-24">
                <h2 className="font-semibold text-hsecondary mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] text-hmuted">Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                    <span className="font-semibold text-[14px] text-hsecondary">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold text-green-600">
                      Will be calculated at checkout
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-hsecondary">Total</span>
                      <span className="text-2xl font-bold text-hprimary">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="w-full rounded-full border-2 border-hsecondary bg-hsecondary hover:bg-hsecondary/80 text-white px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    Proceed to Checkout
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => router.push(buildPath('/shop'))}
                    className="w-full rounded-full border-2 border-muted bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>

                <div className="mt-6 space-y-3 pt-6 border-t border-gray-200">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle2 className="h-5 w-5 text-hmuted shrink-0 mt-0.5" />
                    <span>Secure checkout process</span>
                  </div>
                </div>
              </div>

              {/* <div className="rounded-3xl border border-hprimary-100 bg-hprimary-50/70 p-6 text-sm text-hprimary-700 shadow-xs">
                <p className="font-semibold uppercase tracking-[0.3em]">Need help?</p>
                <p className="mt-2 leading-relaxed">
                  Chat with a pharmacist to optimize your regimen or discuss substitutions before you
                  check out.
                </p>
              </div> */}
            </motion.aside>
          </div>

          {/* Recommended Section - Bottom of populated cart */}
          <RecommendedProductsSection
            title="Recommended for you"
            subtitle=""
            initialLimit={4}
            className=" pt-12 mt-12"
          />
        </div>
      </div>

      {/* Quantity Adjustment Modal */}
      <Modal
        isOpen={showQuantityModal}
        onClose={() => setShowQuantityModal(false)}
        title="Adjust Quantity"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            The following items exceed the available quantity. Please adjust the quantity to continue:
          </p>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {quantityAdjustItems.map(item => {
              const v = item.productVariantData;
              const maxQty = typeof v.inventoryCount === 'number' ? v.inventoryCount : 0;

              return (
                <div
                  key={item.productVariantId}
                  className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl p-3"
                >
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    <Image
                      src={v.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                      alt={v.name}
                      width={56}
                      height={56}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Name and availability */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{v.name}</p>
                    <p className="text-xs text-amber-600 font-medium mt-0.5">
                      Only {maxQty} available — you have {item.quantity}
                    </p>
                  </div>

                  {/* Quantity controls - Only Reduce to button, no increment/decrement */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        // Directly reduce to available quantity
                        updateQuantity(item.productVariantId, maxQty);
                      }}
                      disabled={cartIsLoading || item.quantity === maxQty}
                      className="text-xs font-semibold text-amber-600 border border-amber-200 bg-amber-50 rounded-full px-2.5 py-1.5 hover:bg-amber-100 transition-colors disabled:opacity-50"
                    >
                      Reduce to {maxQty}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowQuantityModal(false)}
              className="px-6 py-2.5 rounded-full bg-hsecondary text-white font-medium hover:shadow-lg transition-all"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
